import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getLocale, LocaleType } from '../locales';
import { calculateAdjustedPxPerInch, estimateBaseDPR } from '../utils/screenDetection';

interface TriangleRulerProps {
  screenSize: number;
  locale?: LocaleType;
  unit?: 'cm' | 'in';
  calibratedPPI?: number | null;
  calibratedDPR?: number;
  resetKey?: number;
}

// Marker state for interactive measurement
interface Markers {
  hStart: number; // horizontal start position (CSS px from ruler origin)
  hEnd: number;   // horizontal end position
  vStart: number; // vertical start position
  vEnd: number;   // vertical end position
}

type DragTarget =
  | 'hStart' | 'hEnd' | 'hBand'
  | 'vStart' | 'vEnd' | 'vBand'
  | null;

const RULER_THICKNESS = 56; // px
const MARKER_SIZE = 16;     // triangle marker size
const HIT_RADIUS = 18;      // touch/click hit area radius
const SCROLL_MULTIPLIER = 5; // page is 5x viewport for long measurements

const DEFAULT_MARKERS: Markers = {
  hStart: 80,
  hEnd: 280,
  vStart: 80,
  vEnd: 280,
};

const TriangleRuler: React.FC<TriangleRulerProps> = ({
  screenSize,
  locale = 'en',
  unit = 'in',
  calibratedPPI = null,
  calibratedDPR = 0,
  resetKey = 0,
}) => {
  const t = getLocale(locale);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseDPR = useRef(estimateBaseDPR());
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [currentDPR, setCurrentDPR] = useState(window.devicePixelRatio);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  // Scroll state for extended vertical measurements
  const [scrollY, setScrollY] = useState(0);

  // Measurement markers
  const [markers, setMarkers] = useState<Markers>({ ...DEFAULT_MARKERS });

  // Reset markers when resetKey changes
  useEffect(() => {
    if (resetKey > 0) {
      setMarkers({ ...DEFAULT_MARKERS });
    }
  }, [resetKey]);

  const dragTarget = useRef<DragTarget>(null);
  const dragOffset = useRef(0); // offset from pointer to marker center during drag
  const bandDragStart = useRef(0); // for band dragging
  const bandMarkerStarts = useRef({ start: 0, end: 0 }); // original positions when band drag started

  // ─── Set up scrollable page for long measurements (vertical only) ───
  useEffect(() => {
    document.body.style.minHeight = `${window.innerHeight * SCROLL_MULTIPLIER}px`;
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.body.style.minHeight = '';
      document.body.style.overflowX = '';
      document.documentElement.style.overflowX = '';
      window.removeEventListener('scroll', handleScroll);
    };
  }, [viewportHeight]);

  // Forward wheel events to page scroll (canvas blocks native scroll)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleWheel = (e: WheelEvent) => {
      if (!dragTarget.current) {
        window.scrollBy(0, e.deltaY);
      }
    };
    canvas.addEventListener('wheel', handleWheel, { passive: true });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  // ─── Zoom-compensated pixels-per-unit ───
  const getPixelsPerUnit = useCallback(() => {
    let pxPerInch: number;
    if (calibratedPPI && calibratedDPR > 0) {
      const zoomFactor = window.devicePixelRatio / calibratedDPR;
      pxPerInch = calibratedPPI / zoomFactor;
    } else {
      pxPerInch = calculateAdjustedPxPerInch(screenSize, baseDPR.current);
    }
    return unit === 'cm' ? pxPerInch / 2.54 : pxPerInch;
  }, [screenSize, unit, calibratedPPI, calibratedDPR]);

  // ─── Hit detection (vertical markers in page space, horizontal in viewport) ───
  const hitTest = useCallback((clientX: number, clientY: number): DragTarget => {
    const rt = RULER_THICKNESS;
    const m = markers;

    // Horizontal markers: no scroll offset (no horizontal scroll)
    // Check horizontal markers (along top ruler, y near rt)
    if (clientY <= rt + HIT_RADIUS) {
      if (Math.abs(clientX - (rt + m.hStart)) < HIT_RADIUS) return 'hStart';
      if (Math.abs(clientX - (rt + m.hEnd)) < HIT_RADIUS) return 'hEnd';
      const hMin = Math.min(m.hStart, m.hEnd);
      const hMax = Math.max(m.hStart, m.hEnd);
      if (clientX > rt + hMin + HIT_RADIUS && clientX < rt + hMax - HIT_RADIUS && clientY >= rt - 10) {
        return 'hBand';
      }
    }

    // Vertical markers: convert page space to viewport space
    const vStartVP = m.vStart - scrollY;
    const vEndVP = m.vEnd - scrollY;

    if (clientX <= rt + HIT_RADIUS) {
      if (Math.abs(clientY - (rt + vStartVP)) < HIT_RADIUS) return 'vStart';
      if (Math.abs(clientY - (rt + vEndVP)) < HIT_RADIUS) return 'vEnd';
      const vMin = Math.min(vStartVP, vEndVP);
      const vMax = Math.max(vStartVP, vEndVP);
      if (clientY > rt + vMin + HIT_RADIUS && clientY < rt + vMax - HIT_RADIUS && clientX >= rt - 10) {
        return 'vBand';
      }
    }

    return null;
  }, [markers, scrollY]);

  // ─── Pointer event handlers ───
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const target = hitTest(e.clientX, e.clientY);
    if (!target) return;

    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragTarget.current = target;

    const rt = RULER_THICKNESS;
    if (target === 'hStart') {
      dragOffset.current = e.clientX - (rt + markers.hStart);
    } else if (target === 'hEnd') {
      dragOffset.current = e.clientX - (rt + markers.hEnd);
    } else if (target === 'vStart') {
      dragOffset.current = e.clientY - (rt + markers.vStart - scrollY);
    } else if (target === 'vEnd') {
      dragOffset.current = e.clientY - (rt + markers.vEnd - scrollY);
    } else if (target === 'hBand') {
      bandDragStart.current = e.clientX;
      bandMarkerStarts.current = { start: markers.hStart, end: markers.hEnd };
    } else if (target === 'vBand') {
      bandDragStart.current = e.clientY;
      bandMarkerStarts.current = { start: markers.vStart, end: markers.vEnd };
    }
  }, [hitTest, markers, scrollY]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const target = dragTarget.current;
    if (!target) {
      // Update cursor based on what's under pointer
      const hover = hitTest(e.clientX, e.clientY);
      const canvas = canvasRef.current;
      if (canvas) {
        if (hover === 'hBand' || hover === 'vBand') {
          canvas.style.cursor = hover === 'hBand' ? 'grab' : 'grab';
        } else if (hover) {
          canvas.style.cursor = hover.startsWith('h') ? 'ew-resize' : 'ns-resize';
        } else {
          canvas.style.cursor = 'default';
        }
      }
      return;
    }

    e.preventDefault();
    const rt = RULER_THICKNESS;
    const maxH = window.innerWidth - rt - 10;
    const maxV = window.innerHeight * SCROLL_MULTIPLIER - rt - 10;

    setMarkers(prev => {
      const next = { ...prev };
      if (target === 'hStart') {
        next.hStart = Math.max(0, Math.min(maxH, e.clientX - rt - dragOffset.current));
      } else if (target === 'hEnd') {
        next.hEnd = Math.max(0, Math.min(maxH, e.clientX - rt - dragOffset.current));
      } else if (target === 'vStart') {
        next.vStart = Math.max(0, Math.min(maxV, e.clientY - rt - dragOffset.current + scrollY));
      } else if (target === 'vEnd') {
        next.vEnd = Math.max(0, Math.min(maxV, e.clientY - rt - dragOffset.current + scrollY));
      } else if (target === 'hBand') {
        const dx = e.clientX - bandDragStart.current;
        const s = bandMarkerStarts.current.start + dx;
        const en = bandMarkerStarts.current.end + dx;
        if (s >= 0 && en >= 0 && s <= maxH && en <= maxH) {
          next.hStart = s;
          next.hEnd = en;
        }
      } else if (target === 'vBand') {
        const dy = e.clientY - bandDragStart.current;
        const s = bandMarkerStarts.current.start + dy;
        const en = bandMarkerStarts.current.end + dy;
        if (s >= 0 && en >= 0 && s <= maxV && en <= maxV) {
          next.vStart = s;
          next.vEnd = en;
        }
      }
      return next;
    });
  }, [hitTest, scrollY]);

  const handlePointerUp = useCallback(() => {
    dragTarget.current = null;
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = 'default';
  }, []);

  // ─── Draw the ruler on canvas ───
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio;
    const w = window.innerWidth;
    const h = window.innerHeight;

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    // Clear background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // Grid dots removed for clean minimal look
    drawGridDots(ctx, w, h, RULER_THICKNESS);

    const pxPerUnit = getPixelsPerUnit();
    const isMobile = w <= 768;
    const fontSize = isMobile ? 10 : 12;
    const fontBold = isMobile ? 11 : 13;

    // Convert only vertical markers from page space to viewport space
    const vpMarkers: Markers = {
      hStart: markers.hStart,
      hEnd: markers.hEnd,
      vStart: markers.vStart - scrollY,
      vEnd: markers.vEnd - scrollY,
    };

    // Draw measurement highlights FIRST (behind the rulers)
    drawMeasurementHighlight(ctx, w, h, vpMarkers, RULER_THICKNESS);

    // Draw rulers — horizontal has no scroll, vertical scrolls
    drawHorizontalRuler(ctx, w, pxPerUnit, RULER_THICKNESS, unit, fontSize, fontBold, 0);
    drawVerticalRuler(ctx, h, pxPerUnit, RULER_THICKNESS, unit, fontSize, fontBold, scrollY);

    // Draw corner square
    drawCornerSquare(ctx, RULER_THICKNESS, unit);

    // Draw measurement markers (viewport space)
    drawMarkers(ctx, vpMarkers, RULER_THICKNESS, w, h);

    // Draw measurement readouts — distance uses page-space delta (same as viewport delta)
    drawMeasurementReadout(ctx, vpMarkers, pxPerUnit, RULER_THICKNESS, unit, isMobile, locale);

    // Draw diagonal line with measurements
    drawDiagonal(ctx, w, h, pxPerUnit, RULER_THICKNESS, unit, isMobile, fontBold);

    // Draw info bar
    drawInfoBar(ctx, w, h, pxPerUnit, unit, isMobile, t, isFullscreen);
  }, [getPixelsPerUnit, unit, t, isFullscreen, markers, locale, scrollY]);

  // ─── Monitor viewport, DPR, and fullscreen changes ───
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
      setCurrentDPR(window.devicePixelRatio);
    };

    const handleFullscreen = () => {
      setTimeout(() => {
        setIsFullscreen(!!document.fullscreenElement);
        handleResize();
      }, 100);
    };

    const dprInterval = setInterval(() => {
      if (window.devicePixelRatio !== currentDPR) {
        setCurrentDPR(window.devicePixelRatio);
      }
    }, 300);

    window.addEventListener('resize', handleResize);
    document.addEventListener('fullscreenchange', handleFullscreen);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreen);
      clearInterval(dprInterval);
    };
  }, [currentDPR]);

  useEffect(() => {
    draw();
  }, [draw, viewportWidth, viewportHeight, currentDPR, screenSize, unit]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0"
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    />
  );
};

// ═══════════════════════════════════════════════════════════════
// Drawing helper functions — Premium Dark Theme
// ═══════════════════════════════════════════════════════════════

function drawGridDots(
  _ctx: CanvasRenderingContext2D,
  _width: number,
  _height: number,
  _rulerThickness: number,
) {
  // Intentionally empty — clean minimal background, no grid dots
}

function drawCornerSquare(ctx: CanvasRenderingContext2D, thickness: number, unit: 'cm' | 'in') {
  // Dark gradient matching rulers
  const grad = ctx.createLinearGradient(0, 0, thickness, thickness);
  grad.addColorStop(0, '#1e293b');
  grad.addColorStop(1, '#283548');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, thickness, thickness);

  // Subtle inner border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, thickness - 1, thickness - 1);

  // Unit label text
  const label = unit === 'cm' ? 'cm' : 'in';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.font = 'bold 13px "SF Mono", "Cascadia Code", "Fira Code", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, thickness / 2, thickness / 2);
}

function drawHorizontalRuler(
  ctx: CanvasRenderingContext2D,
  width: number,
  pxPerUnit: number,
  thickness: number,
  unit: 'cm' | 'in',
  _fontSize: number,
  fontBold: number,
  scrollOffset: number,
) {
  // Dark sleek ruler background
  const grad = ctx.createLinearGradient(0, 0, 0, thickness);
  grad.addColorStop(0, '#1e293b');
  grad.addColorStop(1, '#283548');
  ctx.fillStyle = grad;
  ctx.fillRect(thickness, 0, width - thickness, thickness);

  // Bottom edge highlight
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(thickness, thickness - 0.5);
  ctx.lineTo(width, thickness - 0.5);
  ctx.stroke();

  const markIntervals = getMarkIntervals(unit);
  // Calculate visible range in unit space accounting for scroll
  const firstTickIndex = Math.max(0, Math.floor((scrollOffset / pxPerUnit) / markIntervals.smallest));

  for (let i = firstTickIndex; ; i++) {
    const unitValue = i * markIntervals.smallest;
    // Convert unit to viewport pixel position
    const x = thickness + (unitValue * pxPerUnit - scrollOffset);
    if (x > width + 1) break;
    if (x < thickness - 1) continue;

    const { height: markH, color, lineWidth: lw } = getMarkStyle(unitValue, markIntervals, thickness);

    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(x, thickness);
    ctx.lineTo(x, thickness - markH);
    ctx.stroke();

    // Number labels at major marks
    if (Math.abs(unitValue % markIntervals.major) < 0.001 && unitValue > 0) {
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${fontBold}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(formatUnitLabel(unitValue, unit), x, 5);
    }
  }
}

function drawVerticalRuler(
  ctx: CanvasRenderingContext2D,
  height: number,
  pxPerUnit: number,
  thickness: number,
  unit: 'cm' | 'in',
  _fontSize: number,
  fontBold: number,
  scrollOffset: number,
) {
  // Dark sleek ruler background
  const grad = ctx.createLinearGradient(0, 0, thickness, 0);
  grad.addColorStop(0, '#1e293b');
  grad.addColorStop(1, '#283548');
  ctx.fillStyle = grad;
  ctx.fillRect(0, thickness, thickness, height - thickness);

  // Right edge highlight
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(thickness - 0.5, thickness);
  ctx.lineTo(thickness - 0.5, height);
  ctx.stroke();

  const markIntervals = getMarkIntervals(unit);
  // Calculate visible range accounting for scroll
  const firstTickIndex = Math.max(0, Math.floor((scrollOffset / pxPerUnit) / markIntervals.smallest));

  for (let i = firstTickIndex; ; i++) {
    const unitValue = i * markIntervals.smallest;
    const y = thickness + (unitValue * pxPerUnit - scrollOffset);
    if (y > height + 1) break;
    if (y < thickness - 1) continue;

    const { height: markH, color, lineWidth: lw } = getMarkStyle(unitValue, markIntervals, thickness);

    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(thickness, y);
    ctx.lineTo(thickness - markH, y);
    ctx.stroke();

    if (Math.abs(unitValue % markIntervals.major) < 0.001 && unitValue > 0) {
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${fontBold}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
      ctx.save();
      ctx.translate(9, y);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(formatUnitLabel(unitValue, unit), 0, 0);
      ctx.restore();
    }
  }
}

function drawMeasurementHighlight(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  markers: Markers,
  rt: number,
) {
  // Horizontal measurement zone — very soft blue band
  const hMin = Math.min(markers.hStart, markers.hEnd);
  const hMax = Math.max(markers.hStart, markers.hEnd);
  ctx.fillStyle = 'rgba(59, 130, 246, 0.04)';
  ctx.fillRect(rt + hMin, rt, hMax - hMin, height - rt);

  // Vertical measurement zone — very soft emerald band
  const vMin = Math.min(markers.vStart, markers.vEnd);
  const vMax = Math.max(markers.vStart, markers.vEnd);
  ctx.fillStyle = 'rgba(16, 185, 129, 0.04)';
  ctx.fillRect(rt, rt + vMin, width - rt, vMax - vMin);

  // No intersection highlight — cleaner look

  // Horizontal guide lines (elegant dashed)
  ctx.save();
  ctx.setLineDash([6, 4]);

  ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(rt + markers.hStart, rt);
  ctx.lineTo(rt + markers.hStart, height - 32);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(rt + markers.hEnd, rt);
  ctx.lineTo(rt + markers.hEnd, height - 32);
  ctx.stroke();

  // Vertical guide lines (elegant dashed)
  ctx.strokeStyle = 'rgba(16, 185, 129, 0.2)';
  ctx.beginPath();
  ctx.moveTo(rt, rt + markers.vStart);
  ctx.lineTo(width, rt + markers.vStart);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(rt, rt + markers.vEnd);
  ctx.lineTo(width, rt + markers.vEnd);
  ctx.stroke();

  ctx.restore();
}

function drawMarkers(
  ctx: CanvasRenderingContext2D,
  markers: Markers,
  rt: number,
  _width: number,
  _height: number,
) {
  // ─── Horizontal markers (triangle pointing down from ruler bottom) ───
  drawTriangleMarker(ctx, rt + markers.hStart, rt, 'down', '#3b82f6', '#3b82f6');
  drawTriangleMarker(ctx, rt + markers.hEnd, rt, 'down', '#3b82f6', '#3b82f6');

  // Horizontal measurement band on the ruler
  const hMin = Math.min(markers.hStart, markers.hEnd);
  const hMax = Math.max(markers.hStart, markers.hEnd);
  ctx.fillStyle = 'rgba(59, 130, 246, 0.18)';
  ctx.fillRect(rt + hMin, rt - MARKER_SIZE + 2, hMax - hMin, MARKER_SIZE - 2);

  // ─── Vertical markers (triangle pointing right from ruler right edge) ───
  drawTriangleMarker(ctx, rt, rt + markers.vStart, 'right', '#10b981', '#10b981');
  drawTriangleMarker(ctx, rt, rt + markers.vEnd, 'right', '#10b981', '#10b981');

  // Vertical measurement band on the ruler
  const vMin = Math.min(markers.vStart, markers.vEnd);
  const vMax = Math.max(markers.vStart, markers.vEnd);
  ctx.fillStyle = 'rgba(16, 185, 129, 0.18)';
  ctx.fillRect(rt - MARKER_SIZE + 2, rt + vMin, MARKER_SIZE - 2, vMax - vMin);
}

function drawTriangleMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  direction: 'down' | 'right',
  strokeColor: string,
  fillColor: string,
) {
  const s = MARKER_SIZE;
  ctx.save();

  // Drop shadow for depth
  ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = direction === 'right' ? 1 : 0;
  ctx.shadowOffsetY = direction === 'down' ? 1 : 0;

  ctx.beginPath();
  if (direction === 'down') {
    ctx.moveTo(x, y + 3);
    ctx.lineTo(x - s / 2, y - s + 3);
    ctx.lineTo(x + s / 2, y - s + 3);
  } else {
    ctx.moveTo(x + 3, y);
    ctx.lineTo(x - s + 3, y - s / 2);
    ctx.lineTo(x - s + 3, y + s / 2);
  }
  ctx.closePath();

  // Solid filled marker
  ctx.fillStyle = fillColor;
  ctx.fill();

  // Reset shadow before stroke
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Bright edge stroke
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Subtle glow ring at tip
  ctx.beginPath();
  if (direction === 'down') {
    ctx.arc(x, y + 3, 2.5, 0, Math.PI * 2);
  } else {
    ctx.arc(x + 3, y, 2.5, 0, Math.PI * 2);
  }
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.fill();

  // Inner dot
  ctx.beginPath();
  if (direction === 'down') {
    ctx.arc(x, y + 3, 1.2, 0, Math.PI * 2);
  } else {
    ctx.arc(x + 3, y, 1.2, 0, Math.PI * 2);
  }
  ctx.fillStyle = strokeColor;
  ctx.fill();

  ctx.restore();
}

function drawMeasurementReadout(
  ctx: CanvasRenderingContext2D,
  markers: Markers,
  pxPerUnit: number,
  rt: number,
  unit: 'cm' | 'in',
  isMobile: boolean,
  locale: string,
) {
  const fontSize = isMobile ? 14 : 16;
  const smallFontSize = isMobile ? 10 : 11;

  // ─── Horizontal measurement readout ───
  const hDist = Math.abs(markers.hEnd - markers.hStart) / pxPerUnit;
  const hMid = rt + (markers.hStart + markers.hEnd) / 2;
  const hMin = Math.min(markers.hStart, markers.hEnd);
  const hMax = Math.max(markers.hStart, markers.hEnd);

  if (hMax - hMin > 40) {
    // Measurement line with arrows
    const lineY = rt + 22;
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(rt + hMin + 4, lineY);
    ctx.lineTo(rt + hMax - 4, lineY);
    ctx.stroke();

    // Arrowheads
    drawArrowhead(ctx, rt + hMin + 4, lineY, 'right', '#3b82f6');
    drawArrowhead(ctx, rt + hMax - 4, lineY, 'left', '#3b82f6');

    // Distance label with background
    const label = formatMeasurement(hDist, unit, locale);
    ctx.font = `600 ${fontSize}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
    const textWidth = ctx.measureText(label).width;
    const labelX = hMid;
    const labelY = rt + 40;

    // Background pill with shadow
    const pillPadH = 10;
    const pillPadV = 5;
    ctx.save();
    ctx.shadowColor = 'rgba(59, 130, 246, 0.3)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = 'rgba(59, 130, 246, 0.95)';
    roundRect(ctx, labelX - textWidth / 2 - pillPadH, labelY - fontSize / 2 - pillPadV,
      textWidth + pillPadH * 2, fontSize + pillPadV * 2, 8);
    ctx.fill();
    ctx.restore();

    // Text
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, labelX, labelY);

    // Also show mm if in cm mode
    if (unit === 'cm') {
      const mmLabel = `${(hDist * 10).toFixed(1)} mm`;
      ctx.font = `${smallFontSize}px -apple-system, sans-serif`;
      ctx.fillStyle = 'rgba(59, 130, 246, 0.5)';
      ctx.fillText(mmLabel, labelX, labelY + fontSize + 5);
    }
  }

  // ─── Vertical measurement readout ───
  const vDist = Math.abs(markers.vEnd - markers.vStart) / pxPerUnit;
  const vMid = rt + (markers.vStart + markers.vEnd) / 2;
  const vMin = Math.min(markers.vStart, markers.vEnd);
  const vMax = Math.max(markers.vStart, markers.vEnd);

  if (vMax - vMin > 40) {
    // Measurement line with arrows
    const lineX = rt + 22;
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(lineX, rt + vMin + 4);
    ctx.lineTo(lineX, rt + vMax - 4);
    ctx.stroke();

    // Arrowheads
    drawArrowhead(ctx, lineX, rt + vMin + 4, 'down', '#10b981');
    drawArrowhead(ctx, lineX, rt + vMax - 4, 'up', '#10b981');

    // Distance label with background
    const label = formatMeasurement(vDist, unit, locale);
    ctx.font = `600 ${fontSize}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
    const textWidth = ctx.measureText(label).width;
    const labelX = rt + 40;
    const labelY = vMid;

    // Background pill with shadow
    const pillPadH = 10;
    const pillPadV = 5;
    ctx.save();
    ctx.shadowColor = 'rgba(16, 185, 129, 0.3)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = 'rgba(16, 185, 129, 0.95)';
    roundRect(ctx, labelX - pillPadH, labelY - fontSize / 2 - pillPadV,
      textWidth + pillPadH * 2, fontSize + pillPadV * 2, 8);
    ctx.fill();
    ctx.restore();

    // Text
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, labelX, labelY);

    // mm label
    if (unit === 'cm') {
      const mmLabel = `${(vDist * 10).toFixed(1)} mm`;
      ctx.font = `${smallFontSize}px -apple-system, sans-serif`;
      ctx.fillStyle = 'rgba(16, 185, 129, 0.5)';
      ctx.textAlign = 'left';
      ctx.fillText(mmLabel, labelX, labelY + fontSize + 5);
    }
  }

  // ─── Central measurement summary box ───
  drawMeasurementSummary(ctx, markers, pxPerUnit, rt, unit, isMobile, locale);
}

function drawMeasurementSummary(
  ctx: CanvasRenderingContext2D,
  markers: Markers,
  pxPerUnit: number,
  rt: number,
  unit: 'cm' | 'in',
  isMobile: boolean,
  locale: string,
) {
  const hDist = Math.abs(markers.hEnd - markers.hStart) / pxPerUnit;
  const vDist = Math.abs(markers.vEnd - markers.vStart) / pxPerUnit;

  // Position: center of the measurement intersection area
  const hMid = rt + (markers.hStart + markers.hEnd) / 2;
  const vMid = rt + (markers.vStart + markers.vEnd) / 2;

  // Only show if both measurements are significant
  const hRange = Math.abs(markers.hEnd - markers.hStart);
  const vRange = Math.abs(markers.vEnd - markers.vStart);
  if (hRange < 60 || vRange < 60) return;

  const fontSize = isMobile ? 13 : 15;

  const hLabel = `↔ ${formatMeasurement(hDist, unit, locale)}`;
  const vLabel = `↕ ${formatMeasurement(vDist, unit, locale)}`;

  ctx.font = `600 ${fontSize}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
  const hWidth = ctx.measureText(hLabel).width;
  const vWidth = ctx.measureText(vLabel).width;
  const maxWidth = Math.max(hWidth, vWidth);

  const accentBarW = 4;
  const boxPad = 20;
  const boxW = maxWidth + boxPad * 2 + accentBarW;
  const boxH = isMobile ? 54 : 60;
  const boxX = Math.max(rt + 80, Math.min(hMid - boxW / 2, window.innerWidth - boxW - 20));
  const boxY = Math.max(rt + 60, Math.min(vMid - boxH / 2, window.innerHeight - boxH - 60));

  // Frosted glass background with shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 4;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
  roundRect(ctx, boxX, boxY, boxW, boxH, 14);
  ctx.fill();
  ctx.restore();

  // Subtle border
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.06)';
  ctx.lineWidth = 1;
  roundRect(ctx, boxX, boxY, boxW, boxH, 14);
  ctx.stroke();

  // Blue accent bar (left side, top half)
  ctx.save();
  ctx.beginPath();
  roundRect(ctx, boxX, boxY, boxW, boxH, 14);
  ctx.clip();
  ctx.fillStyle = '#3b82f6';
  ctx.fillRect(boxX, boxY + 8, accentBarW, boxH / 2 - 8);
  // Green accent bar (left side, bottom half)
  ctx.fillStyle = '#10b981';
  ctx.fillRect(boxX, boxY + boxH / 2, accentBarW, boxH / 2 - 8);
  ctx.restore();

  // Horizontal measurement
  ctx.fillStyle = '#3b82f6';
  ctx.font = `600 ${fontSize}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(hLabel, boxX + accentBarW / 2 + boxW / 2, boxY + boxH / 2 - (isMobile ? 9 : 11));

  // Vertical measurement
  ctx.fillStyle = '#10b981';
  ctx.fillText(vLabel, boxX + accentBarW / 2 + boxW / 2, boxY + boxH / 2 + (isMobile ? 9 : 11));
}

function drawArrowhead(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  direction: 'left' | 'right' | 'up' | 'down',
  color: string,
) {
  const size = 6;
  ctx.fillStyle = color;
  ctx.beginPath();
  switch (direction) {
    case 'right':
      ctx.moveTo(x, y);
      ctx.lineTo(x + size, y - size / 2);
      ctx.lineTo(x + size, y + size / 2);
      break;
    case 'left':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size, y - size / 2);
      ctx.lineTo(x - size, y + size / 2);
      break;
    case 'down':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size / 2, y + size);
      ctx.lineTo(x + size / 2, y + size);
      break;
    case 'up':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size / 2, y - size);
      ctx.lineTo(x + size / 2, y - size);
      break;
  }
  ctx.closePath();
  ctx.fill();
}

function drawDiagonal(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  _pxPerUnit: number,
  rulerThickness: number,
  _unit: 'cm' | 'in',
  _isMobile: boolean,
  _fontBold: number,
) {
  const startX = rulerThickness;
  const startY = rulerThickness;
  const endX = width;
  const endY = height - 32;

  // Very faint dotted diagonal line — no tick marks
  ctx.save();
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)';
  ctx.lineWidth = 0.5;
  ctx.setLineDash([2, 6]);
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

function drawInfoBar(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  pxPerUnit: number,
  unit: 'cm' | 'in',
  isMobile: boolean,
  t: any,
  isFullscreen: boolean,
) {
  const rulerThickness = RULER_THICKNESS;
  const visibleWidth = (width - rulerThickness) / pxPerUnit;
  const visibleHeight = (height - rulerThickness) / pxPerUnit;
  const diagonal = Math.sqrt(visibleWidth * visibleWidth + visibleHeight * visibleHeight);

  const barH = isMobile ? 28 : 32;
  const barY = height - barH;

  // Dark themed background matching rulers
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(0, barY, width, barH);

  // Top edge subtle highlight
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(0, barY + 0.5);
  ctx.lineTo(width, barY + 0.5);
  ctx.stroke();

  const unitLabel = unit === 'cm' ? 'cm' : 'in';
  const sep = '  │  ';
  const text =
    `${t.measurement.width}: ${visibleWidth.toFixed(1)} ${unitLabel}` + sep +
    `${t.measurement.height}: ${visibleHeight.toFixed(1)} ${unitLabel}` + sep +
    `${t.measurement.diagonal}: ${diagonal.toFixed(1)} ${unitLabel}` + sep +
    `${isFullscreen ? t.common.fullscreenMode : t.common.window} ${window.innerWidth}×${window.innerHeight}px`;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.font = `${isMobile ? 10 : 11}px "SF Pro Display", -apple-system, "Segoe UI", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, barY + barH / 2);
}

// ═══════════════════════════════════════════════════════════════
// Utility functions
// ═══════════════════════════════════════════════════════════════

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function formatMeasurement(value: number, unit: 'cm' | 'in', _locale: string): string {
  const unitLabel = unit === 'cm' ? 'cm' : 'in';
  if (unit === 'in') {
    // Show fractions for inches
    const whole = Math.floor(value);
    const frac = value - whole;
    if (frac < 0.0625) return `${whole} ${unitLabel}`;

    // Find closest 1/16
    const sixteenths = Math.round(frac * 16);
    if (sixteenths === 16) return `${whole + 1} ${unitLabel}`;

    // Simplify fraction
    let num = sixteenths;
    let den = 16;
    while (num % 2 === 0 && den > 1) { num /= 2; den /= 2; }

    if (whole === 0) return `${num}/${den} ${unitLabel}`;
    return `${whole} ${num}/${den} ${unitLabel}`;
  }
  return `${value.toFixed(1)} ${unitLabel}`;
}

function formatUnitLabel(value: number, _unit: 'cm' | 'in'): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
}

interface MarkIntervals {
  smallest: number;
  half: number;
  major: number;
}

function getMarkIntervals(unit: 'cm' | 'in'): MarkIntervals {
  if (unit === 'cm') {
    return { smallest: 0.1, half: 0.5, major: 1 };
  } else {
    return { smallest: 0.125, half: 0.5, major: 1 };
  }
}

function getMarkStyle(
  value: number,
  intervals: MarkIntervals,
  rulerThickness: number,
): { height: number; color: string; lineWidth: number } {
  const isMajor = Math.abs(value % intervals.major) < 0.001;
  const isHalf = Math.abs(value % intervals.half) < 0.001;
  const isQuarter = Math.abs(value % 0.25) < 0.001;

  if (isMajor) {
    return { height: rulerThickness * 0.55, color: '#ffffff', lineWidth: 1.5 };
  }
  if (isHalf) {
    return { height: rulerThickness * 0.4, color: '#94a3b8', lineWidth: 1 };
  }
  if (isQuarter) {
    return { height: rulerThickness * 0.3, color: '#475569', lineWidth: 0.75 };
  }
  return { height: rulerThickness * 0.2, color: '#475569', lineWidth: 0.5 };
}

export default TriangleRuler;
